import type { Time } from "~/tools/date-time/time";
import { Impulse, type Scope, batch } from "~/tools/impulse";
import { toArray } from "~/tools/to-array";

interface TimestampCursorValue {
	sourceId: string;
	timestamp: number;
}

export interface TimestampCursorMarker {
	timestamp: number;
	isHighlighted: boolean;
}

export class TimestampCursorState {
	/**
	 * The `cursor` Impulse keeps track of the current cursor position and source.
	 * It allows to synchronize the cursor position between multiple sources.
	 */
	private readonly cursor = Impulse<TimestampCursorValue>();

	/**
	 * The `markers` Impulse keeps track of all the timestamps that have been marked.
	 * It allows to show the markers on multiple consumers (charts, timeline, etc).
	 */
	private readonly markers = Impulse<ReadonlySet<number>>(new Set<number>());

	/**
	 * The `hotkeyPressed` Impulse keeps track of whether the hotkey is currently pressed.
	 */
	private readonly hotkeyPressed = Impulse(false);

	/**
	 * A marker that is currently hovered by the user.
	 * It plays a role in highlighting a marker when the hotkey is pressed.
	 */
	private readonly hoveredMarker = Impulse<number>();

	/**
	 * A marker that is currently banned from being highlighted.
	 * It is necessary to avoid highlighting a just toggled marker.
	 */
	private readonly bannedHighlightMarker = Impulse<number>();

	public isHotkeyPressed(scope: Scope): boolean {
		return this.hotkeyPressed.getValue(scope);
	}

	public setHotkeyPressed(isPressed: boolean): void {
		batch(() => {
			this.hotkeyPressed.setValue(isPressed);

			if (!isPressed) {
				this.bannedHighlightMarker.setValue(undefined);
			}
		});
	}

	public getCursor(scope: Scope, sourceId: string): undefined | number {
		const cursor = this.cursor.getValue(scope);

		if (cursor == null || cursor.sourceId === sourceId) {
			return undefined;
		}

		// don't display the cursor if any marker is highlighted or the cursor is already marked
		if (this.getHighlightedMarker(scope) != null || this.markers.getValue(scope).has(cursor.timestamp)) {
			return undefined;
		}

		return cursor.timestamp;
	}

	public setCursor(time: Time, sourceId: string): void {
		this.cursor.setValue({
			sourceId,
			timestamp: time.valueOf(),
		});
	}

	public resetCursor(): void {
		this.cursor.setValue(undefined);
	}

	public getHighlightedMarker(scope: Scope): undefined | number {
		// marker is not hovered if hotkey is not pressed
		if (!this.isHotkeyPressed(scope)) {
			return undefined;
		}

		const hoveredMarker = this.hoveredMarker.getValue(scope);

		if (
			hoveredMarker != null &&
			hoveredMarker !== this.bannedHighlightMarker.getValue(scope) &&
			this.markers.getValue(scope).has(hoveredMarker)
		) {
			return hoveredMarker;
		}

		return undefined;
	}

	public setHighlightedMarker(time: undefined | Time): void {
		const timestamp = time?.valueOf();

		batch((scope) => {
			if (timestamp == null || this.markers.getValue(scope).has(timestamp)) {
				this.hoveredMarker.setValue(timestamp);
			}

			if (timestamp == null) {
				this.bannedHighlightMarker.setValue(undefined);
			}
		});
	}

	public getAllMarkers(scope: Scope): Array<TimestampCursorMarker> {
		const highlightedMarker = this.getHighlightedMarker(scope);

		return toArray(this.markers.getValue(scope), (timestamp) => ({
			timestamp,
			isHighlighted: timestamp === highlightedMarker,
		}));
	}

	public toggleMarker(time: Time): void {
		batch(() => {
			const timestamp = time.valueOf();

			this.markers.setValue((current) => {
				const clone = new Set(current);

				if (clone.has(timestamp)) {
					clone.delete(timestamp);
				} else {
					clone.add(timestamp);
				}

				return clone;
			});

			this.bannedHighlightMarker.setValue(timestamp);
		});
	}
}
